import type { DataListConfig } from '@components/integrated/data-list';
import { dataListWorkflowRegistry } from '@components/integrated/data-list';

// ---------------------------------------------------------------------------
// Custom cell renderers
// ---------------------------------------------------------------------------

function safeStr(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-assignedTo',
  (_value, row) => {
    const firstName = safeStr(row.PRENOM_PER);
    const lastName = safeStr(row.NOM_PER);
    return (
      <span className="font-medium text-sm">
        {capitalize(firstName)} {capitalize(lastName)}
      </span>
    );
  },
);

// Palette of distinct pill colors — deterministically assigned by hashing the label
const PILL_COLORS = [
  { bg: '#3b82f6', text: '#ffffff' }, // blue
  { bg: '#ef4444', text: '#ffffff' }, // red
  { bg: '#f97316', text: '#ffffff' }, // orange
  { bg: '#8b5cf6', text: '#ffffff' }, // purple
  { bg: '#14b8a6', text: '#ffffff' }, // teal
  { bg: '#ec4899', text: '#ffffff' }, // pink
  { bg: '#eab308', text: '#ffffff' }, // yellow
  { bg: '#06b6d4', text: '#ffffff' }, // cyan
  { bg: '#10b981', text: '#ffffff' }, // emerald
  { bg: '#6366f1', text: '#ffffff' }, // indigo
  { bg: '#a855f7', text: '#ffffff' }, // violet
  { bg: '#f43f5e', text: '#ffffff' }, // rose
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = Math.trunc((hash << 5) - hash + (str.codePointAt(i) ?? 0));
  }
  return Math.abs(hash);
}

function getPillColor(label: string) {
  return PILL_COLORS[hashString(label) % PILL_COLORS.length]!;
}

dataListWorkflowRegistry.registerCellRenderer(
  'claimflow-nature',
  (_value, row) => {
    // Always the task's classification label: ECRAN 'AUTRE' rows are not
    // relabelled "Action libre" here (ECRAN has its own "Scénario" column).
    const label = safeStr(row.CLASSEMENT_LIB);
    if (!label) return <span>-</span>;
    const color = getPillColor(label);
    return (
      <span
        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
        style={{ backgroundColor: color.bg, color: color.text }}
      >
        {label}
      </span>
    );
  },
);

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Factory: returns a fresh config with today's date as the default
 * upper bound for the date filter (show tasks due today or earlier).
 */
export function createTaskListConfig(): DataListConfig {
  const today = new Date();
  const defaultDateAu = [
    String(today.getDate()).padStart(2, '0'),
    String(today.getMonth() + 1).padStart(2, '0'),
    today.getFullYear(),
  ].join('/');

  return {
    id: 'claimflow-tasks',

    dataSource: {
      endpoint: {
        erpEndpoint: '/claimflow/tasks',
        method: 'GET',
      },
      responseMapping: {
        itemsPath: 'data.data',
        totalPath: 'data.total',
      },
      fieldMapping: {
        assignedTo: 'PRENOM_PER',
        dateDebut: 'DATE_DEBUT',
        client: 'NOM_PER_ASS',
        noDossier: 'NCIE_SIN',
        classement: 'CLASSEMENT_LIB',
        type: 'LIBELLE_TYPE_TACHE',
        scenario: 'ECRAN',
        libelle: 'LIBELLE',
      },
    },

    columns: {
      assignedTo: {
        label: 'Assigné à',
        order: 1,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-assignedTo' },
      },
      dateDebut: {
        label: 'Date début',
        order: 2,
        type: 'date',
        typeOptions: { format: 'dd/MM/yyyy' },
      },
      client: {
        label: 'Client',
        order: 3,
        type: 'text',
      },
      noDossier: {
        label: 'N° Dossier',
        order: 4,
        type: 'text',
      },
      classement: {
        label: 'Classement',
        order: 5,
        type: 'custom',
        typeOptions: { renderer: 'claimflow-nature' },
      },
      type: {
        label: 'Type',
        order: 6,
        type: 'text',
      },
      scenario: {
        label: 'Scénario',
        order: 7,
        type: 'text',
      },
      libelle: {
        label: 'Libellé',
        order: 8,
        type: 'text',
        typeOptions: { truncate: 50 },
        cellTooltip: { field: 'LIBELLE' },
      },
    },

    display: {
      title: 'Tâches en cours',
      titleIcon: 'clipboard-list',
      hoverable: true,
    },

    // The card is French throughout, so it must not fall back to the shared
    // English DEFAULT_I18N_KEYS when a fetch fails (DataList.Table renders this
    // as the error-state title). The shared default is deliberately unchanged —
    // it is also used by the admin module.
    i18n: {
      errorMessage: 'Impossible de charger les tâches.',
    },

    // Scope the row hover color to this list only. The base Table.Row applies
    // `hover:bg-secondary-100` when hoverable; the arbitrary `#C9E0E3` (light
    // teal) is an equal-specificity `hover:bg-*` utility, so the `!important`
    // modifier is required — verified via the project's Tailwind compile:
    // `hover:bg-[#C9E0E3]!` emits `background-color: #C9E0E3 !important`, which
    // wins over the non-important `hover:bg-secondary-100` regardless of source
    // order. Rows already carry `transition-colors`, so the hover animates.
    cssOverrides: {
      row: 'hover:bg-[#C9E0E3]!',
    },

    toolbar: {
      filters: [
        {
          id: 'ncbt_sin',
          label: 'N° Sinistre',
          type: 'text',
          placeholder: 'Référence, Gestionnaire, Assuré, Libellé',
        },
        {
          id: 'date_du',
          paramName: 'debut_du',
          label: 'Date début (de)',
          type: 'date',
          placeholder: 'Du…',
          dateFormat: 'dd/MM/yyyy',
        },
        {
          id: 'date_au',
          paramName: 'debut_au',
          label: 'Date début (à)',
          type: 'date',
          placeholder: 'Au…',
          dateFormat: 'dd/MM/yyyy',
          defaultValue: defaultDateAu,
        },
        {
          id: 'nature',
          paramName: 'classement',
          label: 'Type de tâche',
          type: 'select',
          placeholder: 'Tous les types',
          optionsEndpoint: {
            erpEndpoint: '/claimflow/tasks/classifications',
            valuePath: 'COL1',
            labelPath: 'COL2',
          },
        },
      ],
    },

    actions: {
      rowClick: { handler: 'claimflow-taskDetail' },
    },

    sorting: {
      enabled: true,
      mode: 'server',
      sortParam: 'sort',
      directionFormat: 'append-desc',
      positionFromResponse: true,
      persistSort: true,
    },

    pagination: {
      mode: 'pagination',
      pageSize: 100,
      pageSizeParam: 'perPage',
      showPageSize: false,
      showTotal: true,
      persistPage: true,
    },
  };
}
